/**
 * The advisor summary (סקירת יועץ) — shared by server and portal.
 *
 * Bodies are the rich-text editor's JSON document (Tiptap / ProseMirror). The
 * schema below is the whitelist: only the node and mark types the editor
 * actually offers, with exactly the attributes it writes, only safe link
 * targets (http, https, mailto), only the palette's colours. Anything else is
 * refused when it is saved, so what reaches a client's screen or printed
 * report is always something the editor itself could have produced — never
 * raw HTML, never a script link.
 *
 * Browser-safe: no node-only imports.
 */
import { z } from 'zod';
/** Node types the editor offers (StarterKit + tables). */
export const ADVISOR_NODE_TYPES = [
    'doc', 'paragraph', 'text', 'heading', 'blockquote', 'bulletList', 'orderedList', 'listItem',
    'horizontalRule', 'hardBreak', 'codeBlock',
    'table', 'tableRow', 'tableHeader', 'tableCell',
];
/** Mark types the editor offers. */
export const ADVISOR_MARK_TYPES = ['bold', 'italic', 'underline', 'strike', 'code', 'link', 'textStyle'];
/** Link protocols a summary may point at. */
export const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];
export const TEXT_ALIGNMENTS = ['right', 'center', 'left', 'justify'];
/**
 * True only for an absolute http(s) or mailto link. Parsed with the URL
 * parser on purpose: it is what the browser does too, so tricks like
 * "java\tscript:" or " JAVASCRIPT:" resolve to the same protocol here as there.
 */
export function isSafeHref(href) {
    if (typeof href !== 'string' || href.trim() === '')
        return false;
    try {
        const url = new URL(href.trim());
        return SAFE_LINK_PROTOCOLS.includes(url.protocol);
    }
    catch {
        return false;
    }
}
/**
 * The text colours the editor offers — the only colours a summary may carry.
 * Stored as these dark-theme values; the portal re-shades them on light
 * backgrounds (styles.css).
 */
export const ADVISOR_PALETTE = [
    { label: 'כחול', value: '#4aa3ff' },
    { label: 'ירוק', value: '#3ee0a4' },
    { label: 'כתום', value: '#f5a524' },
    { label: 'אדום', value: '#ff6b7a' },
    { label: 'סגול', value: '#c084fc' },
];
const PALETTE_VALUES = ADVISOR_PALETTE.map((c) => c.value);
/**
 * A colour as it may reach the editor — '#4AA3FF', or 'rgb(74, 163, 255)' when
 * the browser has rewritten it (copy and paste does) — back to its palette
 * value. Anything not in the palette is null: it is not a colour we offer.
 */
export function toPaletteColor(value) {
    if (typeof value !== 'string')
        return null;
    const v = value.trim().toLowerCase();
    const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*1(?:\.0+)?\s*)?\)$/.exec(v);
    const hex = rgb
        ? `#${rgb.slice(1, 4).map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`
        : v;
    return PALETTE_VALUES.includes(hex) ? hex : null;
}
// ---- attributes: exactly the keys Tiptap 3.31.4 writes for the extensions we
// load (packages/ui/src/components/advisor/advisorExtensions.ts). A key that is
// not listed is refused; a node or mark that takes no attributes must not
// carry any. packages/ui/test/advisorEditorOutput.test.ts runs the real editor
// and checks that what it writes passes this — if Tiptap starts writing a new
// key, that test fails first.
const align = z.enum(TEXT_ALIGNMENTS).nullable();
const textAlign = { textAlign: align.optional() };
const cellAttrs = z.object({
    colspan: z.number().int().min(1).max(100).optional(),
    rowspan: z.number().int().min(1).max(100).optional(),
    colwidth: z.array(z.number().int().min(0).max(10_000)).max(100).nullable().optional(),
    align: z.enum(['left', 'right', 'center']).nullable().optional(),
}).strict();
const NODE_ATTRS = {
    doc: null,
    text: null,
    paragraph: z.object(textAlign).strict(),
    heading: z.object({ ...textAlign, level: z.union([z.literal(1), z.literal(2), z.literal(3)]) }).strict(),
    blockquote: null,
    bulletList: null,
    orderedList: z.object({
        start: z.number().int().min(0).max(100_000).optional(),
        type: z.enum(['1', 'a', 'A', 'i', 'I']).nullable().optional(),
    }).strict(),
    listItem: null,
    horizontalRule: null,
    hardBreak: null,
    codeBlock: z.object({ language: z.string().regex(/^[\w#+.-]{0,32}$/).nullable().optional() }).strict(),
    table: null,
    tableRow: null,
    tableHeader: cellAttrs,
    tableCell: cellAttrs,
};
const MARK_ATTRS = {
    bold: null,
    italic: null,
    underline: null,
    strike: null,
    code: null,
    link: z.object({
        href: z.string().max(2048).refine(isSafeHref, 'קישור מותר רק לכתובת http, https או mailto'),
        target: z.literal('_blank').nullable().optional(),
        // A link opening a new tab must not hand that tab a handle back to the portal.
        rel: z.literal('noopener noreferrer').nullable().optional(),
        class: z.null().optional(),
        title: z.string().max(500).nullable().optional(),
    }).strict(),
    textStyle: z.object({
        color: z.enum(PALETTE_VALUES, { errorMap: () => ({ message: 'צבע טקסט שאינו מהצבעים המוצעים' }) }).nullable().optional(),
    }).strict(),
};
/** Check `attrs` against the allowlist for `type`, reporting each problem under `attrs`. */
function checkAttrs(type, attrs, allowed, ctx) {
    if (attrs === undefined)
        return;
    if (allowed === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['attrs'], message: `${type} takes no attributes` });
        return;
    }
    const r = allowed.safeParse(attrs);
    if (!r.success)
        for (const issue of r.error.issues)
            ctx.addIssue({ ...issue, path: ['attrs', ...issue.path] });
}
const markSchema = z
    .object({ type: z.enum(ADVISOR_MARK_TYPES), attrs: z.unknown().optional() })
    .strict()
    .superRefine((m, ctx) => {
    checkAttrs(m.type, m.attrs, MARK_ATTRS[m.type], ctx);
    if (m.type === 'link' && m.attrs === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a link needs an address' });
    }
});
const nodeSchema = z.lazy(() => z
    .object({
    type: z.enum(ADVISOR_NODE_TYPES),
    attrs: z.unknown().optional(),
    content: z.array(nodeSchema).max(5000).optional(),
    marks: z.array(markSchema).max(20).optional(),
    text: z.string().max(20000).optional(),
})
    .strict()
    .superRefine((n, ctx) => {
    if (n.type === 'text' && (typeof n.text !== 'string' || n.text === '')) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a text node needs text' });
    }
    if (n.type !== 'text' && n.text !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${n.type} cannot carry text` });
    }
    if (n.type !== 'text' && n.marks !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${n.type} cannot carry marks` });
    }
    if (n.type === 'doc') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a document cannot be nested' });
    }
    if (n.type === 'heading' && n.attrs === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a heading needs a level' });
    }
    checkAttrs(n.type, n.attrs, NODE_ATTRS[n.type], ctx);
}));
export const advisorDocSchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(nodeSchema).max(5000).optional(),
})
    .strict();
/** POST /admin/advisor-summary/versions and PUT …/clients/:id/draft. */
export const advisorBodyRequestSchema = z.object({ body: advisorDocSchema }).strict();
/** POST /admin/advisor-summary/clients/:id/copy. */
export const advisorCopyRequestSchema = z.object({ version: z.number().int().positive() }).strict();
/** True when the document has any visible text (an empty summary must never be published). */
export function docHasText(node) {
    if (!node)
        return false;
    if (node.type === 'text')
        return (node.text ?? '').trim() !== '';
    return (node.content ?? []).some(docHasText);
}
/**
 * Structural equality of two stored bodies. Key order is ignored on purpose:
 * Postgres jsonb does not keep it, so a body read back is "the same" body
 * even when its keys come back in a different order.
 */
export function docsEqual(a, b) {
    if (a === b)
        return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object')
        return false;
    if (Array.isArray(a) !== Array.isArray(b))
        return false;
    if (Array.isArray(a)) {
        const bb = b;
        return a.length === bb.length && a.every((v, i) => docsEqual(v, bb[i]));
    }
    const ao = a;
    const bo = b;
    const keys = Object.keys(ao).filter((k) => ao[k] !== undefined);
    const bKeys = Object.keys(bo).filter((k) => bo[k] !== undefined);
    return keys.length === bKeys.length && keys.every((k) => docsEqual(ao[k], bo[k]));
}
export function advisorStatusFlags(row, latestVersion) {
    const draft = row?.draft ?? null;
    const published = row?.published ?? null;
    const copied = row?.copiedFromVersion ?? null;
    return {
        hasUnpublishedEdits: !docsEqual(draft, published),
        newerVersionAvailable: latestVersion !== null && (copied === null || latestVersion > copied),
    };
}
//# sourceMappingURL=advisorSummary.js.map